import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSafetyNavigate as useNavigate, SafetyLink as Link } from "@/lib/safetyNavigation";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "./_shared";
import {
  ArrowLeft, Lock, Unlock, FileText, Sparkles, Save, Plus, Trash2,
  ShieldAlert, Building2, Users as UsersIcon, AlertTriangle, HardHat,
  ClipboardList, BookOpen, MessageCircle, Siren, LogIn, Truck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function SSSPEditor() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = ["admin", "super_admin"].includes(user.role);
  const [sssp, setSSSP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [crew, setCrew] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/sssps/${id}`);
        if (!cancelled) setSSSP(r.data);
      } catch (e) {
        toast.error("SSSP not found");
        nav("/app/sssps");
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const [jr, tr] = await Promise.all([api.get("/jobs"), api.get("/team/directory")]);
        setJobs(jr.data || []);
        setCrew((tr.data || []).filter((m) => !m.suspended));
      } catch {/* non-fatal */}
    })();
  }, []);

  if (!sssp) return <div className="p-10 text-zinc-500 text-sm">Loading SSSP…</div>;

  const locked = sssp.status === "locked";
  const canEdit = !locked || isAdmin;
  const sections = sssp.sections || {};

  const updateSection = (key, val) => {
    setSSSP({ ...sssp, sections: { ...sections, [key]: val } });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/sssps/${id}`, { sections: sssp.sections });
      setSSSP(r.data);
      setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const aiDraft = async () => {
    if (!window.confirm("AI will fill every empty section based on the current Project Info. Existing content is preserved. Continue?")) return;
    setDrafting(true);
    try {
      const r = await api.post(`/sssps/${id}/ai-draft`);
      setSSSP(r.data);
      toast.success("AI draft applied — review every section");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI draft failed");
    } finally { setDrafting(false); }
  };

  const toggleLock = async () => {
    if (!isAdmin) return;
    if (!locked && dirty) { toast.error("Save your changes before locking"); return; }
    const verb = locked ? "Unlock" : "Lock";
    if (!window.confirm(`${verb} this SSSP?`)) return;
    try {
      const r = await api.post(`/sssps/${id}/lock`);
      setSSSP(r.data);
      toast.success(verb + "ed");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const downloadPdf = async () => {
    try {
      if (canEdit && dirty) await save(); // unsaved edits must reach the PDF
      const res = await api.get(`/sssps/${id}/export.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `sssp_${sssp.name.replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 40)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="sssp-editor" className="p-6 lg:p-10 max-w-5xl mx-auto pb-32 lg:pb-10">
      <Link to="/app/sssps" className="text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5F15] inline-flex items-center gap-1 mb-3" data-testid="sssp-back-link">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to SSSPs
      </Link>
      <PageHeader
        tag={`/ ${locked ? "approved · locked" : "live draft"}`}
        title={sssp.name}
        subtitle={sssp.project_address || "No site address set"}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={downloadPdf} variant="outline" className="rounded-sm border-zinc-900 uppercase tracking-wider font-bold" data-testid="sssp-pdf-button">
              <FileText className="w-4 h-4 mr-2" /> PDF
            </Button>
            {canEdit && (
              <Button onClick={aiDraft} disabled={drafting} variant="outline" className="rounded-sm border-[#FF5F15] text-[#FF5F15] uppercase tracking-wider font-bold hover:bg-[#FFF7ED]" data-testid="sssp-ai-draft-button">
                <Sparkles className="w-4 h-4 mr-2" /> {drafting ? "Drafting…" : "AI re-draft empty"}
              </Button>
            )}
            {canEdit && (
              <Button onClick={save} disabled={saving || !dirty} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold disabled:opacity-50" data-testid="sssp-save-button">
                <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : dirty ? "Save" : "Saved"}
              </Button>
            )}
            {isAdmin && (
              <Button onClick={toggleLock} variant="outline" className={`rounded-sm uppercase tracking-wider font-bold ${locked ? "border-yellow-500 text-yellow-700" : "border-green-600 text-green-700"}`} data-testid="sssp-lock-button">
                {locked ? <><Unlock className="w-4 h-4 mr-2" /> Unlock</> : <><Lock className="w-4 h-4 mr-2" /> Lock as approved</>}
              </Button>
            )}
          </div>
        }
      />

      {locked && !isAdmin && (
        <div className="hard-card p-3 bg-yellow-50 border-yellow-400 text-yellow-900 text-sm mb-4 flex items-center gap-2" data-testid="sssp-locked-banner">
          <Lock className="w-4 h-4" /> This SSSP is locked. Only an admin can edit. You can still read it and export the PDF.
        </div>
      )}

      <div className="space-y-4">
        <ProjectInfoSection canEdit={canEdit} value={sections.project_info || {}} onChange={(v) => updateSection("project_info", v)} jobs={jobs} crew={crew} />
        <TextSection canEdit={canEdit} icon={UsersIcon} title="2. Personnel & responsibilities" value={sections.personnel || ""} onChange={(v) => updateSection("personnel", v)} />
        <HazardsSection canEdit={canEdit} value={sections.hazards || []} onChange={(v) => updateSection("hazards", v)} scope={sections.project_info?.scope_of_works || ""} />
        <EmergencySection canEdit={canEdit} value={sections.emergency || {}} onChange={(v) => updateSection("emergency", v)} />
        <ListSection canEdit={canEdit} icon={HardHat} title="5. PPE required" value={sections.ppe || []} onChange={(v) => updateSection("ppe", v)} placeholder="e.g. Hi-vis Class D/N, hard hat, safety boots…" />
        <ListSection canEdit={canEdit} icon={ClipboardList} title="6. Site rules" value={sections.site_rules || []} onChange={(v) => updateSection("site_rules", v)} placeholder="e.g. No drugs or alcohol on site" />
        <HazSubsSection canEdit={canEdit} value={sections.hazardous_substances || []} onChange={(v) => updateSection("hazardous_substances", v)} />
        <ListSection canEdit={canEdit} icon={BookOpen} title="8. Training & competency required" value={sections.training_required || []} onChange={(v) => updateSection("training_required", v)} placeholder="e.g. Site Safe Passport, Class 2 licence for operators" />
        <TextSection canEdit={canEdit} icon={MessageCircle} title="9. Communications" value={sections.communications || ""} onChange={(v) => updateSection("communications", v)} />
        <ListSection canEdit={canEdit} icon={Siren} title="10. Incident reporting procedure" value={sections.incident_procedure || []} onChange={(v) => updateSection("incident_procedure", v)} placeholder="e.g. Make scene safe, notify supervisor immediately, fill incident report within 1 hour" />
        <TextSection canEdit={canEdit} icon={LogIn} title="11. Site sign-in" value={sections.site_signin || ""} onChange={(v) => updateSection("site_signin", v)} />
        <PlantSection canEdit={canEdit} value={sections.plant_register || []} onChange={(v) => updateSection("plant_register", v)} />
      </div>

      {/* Sticky save bar on mobile */}
      {canEdit && dirty && (
        <div className="fixed bottom-[80px] lg:bottom-4 left-3 right-3 lg:left-1/2 lg:right-auto lg:-translate-x-1/2 z-40 bg-[#09090B] text-white border-4 border-[#FF5F15] p-3 lg:p-4 flex items-center justify-between gap-3 shadow-[6px_6px_0_0_rgba(0,0,0,0.4)]" data-testid="sssp-sticky-save">
          <div className="text-xs font-mono uppercase tracking-widest">Unsaved changes</div>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase font-bold tracking-wider text-xs px-4 py-1.5">
            {saving ? "Saving…" : <><Save className="w-3.5 h-3.5 mr-1.5" /> Save</>}
          </Button>
        </div>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, children, badge }) {
  return (
    <div className="hard-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 bg-[#09090B] flex items-center justify-center shrink-0">
          <Icon className="w-3.5 h-3.5 text-[#FF5F15]" />
        </div>
        <h3 className="font-display font-bold uppercase tracking-tight text-base flex-1">{title}</h3>
        {badge}
      </div>
      {children}
    </div>
  );
}

function ProjectInfoSection({ canEdit, value, onChange, jobs = [], crew = [] }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e?.target ? e.target.value : e });
  const pickJob = (jobId) => {
    if (jobId === "__custom__") {
      onChange({ ...value, job_id: null, project_name: "" });
      return;
    }
    const j = jobs.find((x) => x.id === jobId);
    if (!j) return;
    onChange({
      ...value,
      job_id: j.id,
      project_name: j.title || value.project_name || "",
      site_address: value.site_address || j.site_address || "",
      client_name: value.client_name || j.client_name || "",
      scope_of_works: value.scope_of_works || j.description || "",
    });
  };
  const pickPm = (userId) => {
    if (userId === "__custom__") {
      onChange({ ...value, project_manager_user_id: null });
      return;
    }
    const m = crew.find((x) => x.id === userId);
    if (!m) return;
    onChange({
      ...value,
      project_manager_user_id: m.id,
      project_manager_name: m.name,
    });
  };
  const currentJobId = value.job_id && jobs.some((j) => j.id === value.job_id) ? value.job_id : "__custom__";
  const currentPmId = value.project_manager_user_id && crew.some((m) => m.id === value.project_manager_user_id) ? value.project_manager_user_id : "__custom__";

  return (
    <Section icon={Building2} title="1. Project information">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="label-tag">Project / Job</Label>
          <Select value={currentJobId} onValueChange={pickJob} disabled={!canEdit}>
            <SelectTrigger className="mt-1 rounded-sm border-zinc-400 h-9" data-testid="si-job-select">
              <SelectValue placeholder="Select a job…" />
            </SelectTrigger>
            <SelectContent className="rounded-sm max-h-72">
              <SelectItem value="__custom__">Custom (no linked job)</SelectItem>
              {jobs.map((j) => (
                <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <LabelledInput label="Project name" value={value.project_name || ""} onChange={set("project_name")} disabled={!canEdit} testid="si-project" />
        <LabelledInput label="Client" value={value.client_name || ""} onChange={set("client_name")} disabled={!canEdit} testid="si-client" />
        <LabelledInput label="Site address" value={value.site_address || ""} onChange={set("site_address")} disabled={!canEdit} testid="si-address" />
        <div>
          <Label className="label-tag">Project manager</Label>
          <Select value={currentPmId} onValueChange={pickPm} disabled={!canEdit}>
            <SelectTrigger className="mt-1 rounded-sm border-zinc-400 h-9" data-testid="si-pm-select">
              <SelectValue placeholder="Select a crew member…" />
            </SelectTrigger>
            <SelectContent className="rounded-sm max-h-72">
              <SelectItem value="__custom__">Type a name manually</SelectItem>
              {crew.map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.name} · {m.role}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {currentPmId === "__custom__" && (
            <Input value={value.project_manager_name || ""} onChange={set("project_manager_name")} disabled={!canEdit} placeholder="Manager name" className="mt-1 rounded-sm border-zinc-400" data-testid="si-pm" />
          )}
        </div>
        <LabelledInput label="PM phone" value={value.project_manager_phone || ""} onChange={set("project_manager_phone")} disabled={!canEdit} testid="si-pmphone" />
        <LabelledInput label="Start date" type="date" value={value.start_date || ""} onChange={set("start_date")} disabled={!canEdit} testid="si-start" />
        <LabelledInput label="End date" type="date" value={value.end_date || ""} onChange={set("end_date")} disabled={!canEdit} testid="si-end" />
        <div className="sm:col-span-2">
          <Label className="label-tag">Scope of works</Label>
          <Textarea value={value.scope_of_works || ""} onChange={set("scope_of_works")} rows={3} disabled={!canEdit} className="mt-1 rounded-sm border-zinc-400" data-testid="si-scope" />
        </div>
      </div>
    </Section>
  );
}

function LabelledInput({ label, type = "text", value, onChange, disabled, testid }) {
  return (
    <div>
      <Label className="label-tag">{label}</Label>
      <Input type={type} value={value} onChange={onChange} disabled={disabled} className="mt-1 rounded-sm border-zinc-400" data-testid={testid} />
    </div>
  );
}

function TextSection({ canEdit, icon, title, value, onChange }) {
  return (
    <Section icon={icon} title={title}>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={4} disabled={!canEdit} className="rounded-sm border-zinc-400" placeholder="Write here…" />
    </Section>
  );
}

function ListSection({ canEdit, icon, title, value, onChange, placeholder }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    if (!draft.trim()) return;
    onChange([...(value || []), draft.trim()]);
    setDraft("");
  };
  const remove = (i) => onChange(value.filter((_, j) => j !== i));
  return (
    <Section icon={icon} title={title}>
      <ul className="space-y-1.5">
        {value.length === 0 && <li className="text-sm text-zinc-400 italic">No items yet.</li>}
        {value.map((item, i) => (
          <li key={i} className="flex items-center gap-2 group">
            <span className="w-5 text-xs font-mono text-zinc-400 text-right">{i + 1}.</span>
            {canEdit ? (
              <Input value={item} onChange={(e) => onChange(value.map((v, j) => j === i ? e.target.value : v))} className="rounded-sm border-zinc-300 h-8 text-sm flex-1" />
            ) : (
              <span className="flex-1 text-sm">{item}</span>
            )}
            {canEdit && (
              <button onClick={() => remove(i)} className="text-zinc-400 hover:text-red-600 opacity-0 group-hover:opacity-100" aria-label="Remove">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {canEdit && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-zinc-200">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={placeholder} className="rounded-sm border-zinc-400 h-9 flex-1" />
          <Button type="button" onClick={add} variant="outline" className="rounded-sm h-9 uppercase font-bold text-xs">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>
      )}
    </Section>
  );
}

function HazardsSection({ canEdit, value, onChange, scope }) {
  const [aiBusy, setAiBusy] = useState(false);
  const add = () => onChange([...(value || []), { task: "", hazards: [""], controls: [""], risk_after_controls: "Low" }]);
  const remove = (i) => onChange(value.filter((_, j) => j !== i));
  const setRow = (i, patch) => onChange(value.map((h, j) => j === i ? { ...h, ...patch } : h));

  const aiSuggest = async (i) => {
    const row = value[i];
    if (!row?.task?.trim()) { toast.error("Type the task first, then tap AI"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/sssps/ai-hazard", { task: row.task, scope });
      setRow(i, { hazards: data.hazards || row.hazards, controls: data.controls || row.controls, risk_after_controls: data.risk_after_controls || row.risk_after_controls });
      toast.success("Hazards drafted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI failed");
    } finally { setAiBusy(false); }
  };

  return (
    <Section icon={AlertTriangle} title="3. Hazard register">
      {value.length === 0 && <p className="text-sm text-zinc-400 italic mb-3">No hazards listed yet. Add a task to get started.</p>}
      <div className="space-y-3">
        {value.map((h, i) => (
          <div key={i} className="border-2 border-zinc-200 p-3" data-testid={`hazard-row-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <Input value={h.task || ""} onChange={(e) => setRow(i, { task: e.target.value })} disabled={!canEdit} placeholder="Task (e.g. Excavation work)" className="rounded-sm border-zinc-400 h-9 flex-1 font-bold" />
              {canEdit && (
                <>
                  <Button type="button" size="sm" variant="outline" disabled={aiBusy} onClick={() => aiSuggest(i)} className="rounded-sm h-9 text-[10px] font-bold uppercase tracking-wider text-[#FF5F15] border-[#FF5F15] hover:bg-[#FFF7ED]" data-testid={`hazard-ai-${i}`}>
                    <Sparkles className="w-3 h-3 mr-1" /> AI
                  </Button>
                  <button onClick={() => remove(i)} className="text-zinc-400 hover:text-red-600 p-1.5" aria-label="Remove">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-2">
              <MiniList label="Hazards" items={h.hazards || []} onChange={(v) => setRow(i, { hazards: v })} disabled={!canEdit} placeholder="e.g. Cave-in" />
              <MiniList label="Controls (eliminate / isolate / minimise)" items={h.controls || []} onChange={(v) => setRow(i, { controls: v })} disabled={!canEdit} placeholder="e.g. Shoring system installed" />
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Label className="label-tag">Residual risk</Label>
              <select value={h.risk_after_controls || "Low"} onChange={(e) => setRow(i, { risk_after_controls: e.target.value })} disabled={!canEdit} className="text-xs font-mono border border-zinc-400 rounded-sm h-7 px-2 bg-white">
                <option>Low</option><option>Medium</option><option>High</option>
              </select>
            </div>
          </div>
        ))}
      </div>
      {canEdit && (
        <Button type="button" onClick={add} variant="outline" className="rounded-sm w-full mt-3 uppercase font-bold text-xs">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add task / hazard
        </Button>
      )}
    </Section>
  );
}

function MiniList({ label, items, onChange, disabled, placeholder }) {
  const [draft, setDraft] = useState("");
  const add = () => { if (draft.trim()) { onChange([...items, draft.trim()]); setDraft(""); } };
  return (
    <div>
      <Label className="label-tag">{label}</Label>
      <ul className="mt-1 space-y-1">
        {items.map((x, i) => (
          <li key={i} className="flex items-center gap-1.5">
            <span className="text-zinc-400 text-xs">·</span>
            {disabled ? <span className="text-xs flex-1">{x}</span> : (
              <Input value={x} onChange={(e) => onChange(items.map((v, j) => j === i ? e.target.value : v))} className="rounded-sm border-zinc-200 h-7 text-xs flex-1" />
            )}
            {!disabled && (
              <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-red-600" aria-label="Remove">
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {!disabled && (
        <div className="flex gap-1 mt-1">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={placeholder} className="rounded-sm border-zinc-300 h-7 text-xs flex-1" />
          <Button type="button" size="sm" variant="ghost" onClick={add} className="h-7 text-xs px-2">+</Button>
        </div>
      )}
    </div>
  );
}

function EmergencySection({ canEdit, value, onChange }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e?.target ? e.target.value : e });
  return (
    <Section icon={Siren} title="4. Emergency plan">
      <div className="grid sm:grid-cols-2 gap-3">
        <LabelledInput label="Nearest hospital" value={value.nearest_hospital || ""} onChange={set("nearest_hospital")} disabled={!canEdit} />
        <LabelledInput label="Hospital address" value={value.nearest_hospital_address || ""} onChange={set("nearest_hospital_address")} disabled={!canEdit} />
        <LabelledInput label="Muster point on site" value={value.muster_point || ""} onChange={set("muster_point")} disabled={!canEdit} />
        <LabelledInput label="Site emergency contact" value={value.site_emergency_contact || ""} onChange={set("site_emergency_contact")} disabled={!canEdit} />
        <LabelledInput label="After-hours contact" value={value.after_hours_contact || ""} onChange={set("after_hours_contact")} disabled={!canEdit} />
      </div>
    </Section>
  );
}

function HazSubsSection({ canEdit, value, onChange }) {
  const add = () => onChange([...(value || []), { substance: "", sds_held: "Yes", storage: "" }]);
  const remove = (i) => onChange(value.filter((_, j) => j !== i));
  const setRow = (i, patch) => onChange(value.map((r, j) => j === i ? { ...r, ...patch } : r));
  return (
    <Section icon={ShieldAlert} title="7. Hazardous substances">
      {value.length === 0 && <p className="text-sm text-zinc-400 italic mb-2">None recorded.</p>}
      <div className="space-y-2">
        {value.map((row, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_100px_1fr_30px] gap-2 items-center">
            <Input placeholder="Substance" value={row.substance || ""} onChange={(e) => setRow(i, { substance: e.target.value })} disabled={!canEdit} className="rounded-sm border-zinc-300 h-8 text-sm" />
            <select value={row.sds_held || "Yes"} onChange={(e) => setRow(i, { sds_held: e.target.value })} disabled={!canEdit} className="text-sm border border-zinc-300 rounded-sm h-8 px-2 bg-white">
              <option>Yes</option><option>No</option>
            </select>
            <Input placeholder="Storage on site" value={row.storage || ""} onChange={(e) => setRow(i, { storage: e.target.value })} disabled={!canEdit} className="rounded-sm border-zinc-300 h-8 text-sm" />
            {canEdit && (
              <button onClick={() => remove(i)} className="text-zinc-400 hover:text-red-600" aria-label="Remove">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
      {canEdit && (
        <Button type="button" onClick={add} variant="outline" className="rounded-sm w-full mt-3 uppercase font-bold text-xs">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add substance
        </Button>
      )}
    </Section>
  );
}

function PlantSection({ canEdit, value, onChange }) {
  const add = () => onChange([...(value || []), { item: "", owner: "", wof_expiry: "" }]);
  const remove = (i) => onChange(value.filter((_, j) => j !== i));
  const setRow = (i, patch) => onChange(value.map((r, j) => j === i ? { ...r, ...patch } : r));
  return (
    <Section icon={Truck} title="12. Plant & equipment register">
      {value.length === 0 && <p className="text-sm text-zinc-400 italic mb-2">No plant recorded.</p>}
      <div className="space-y-2">
        {value.map((row, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_140px_30px] gap-2 items-center">
            <Input placeholder="Item (e.g. 5T excavator)" value={row.item || ""} onChange={(e) => setRow(i, { item: e.target.value })} disabled={!canEdit} className="rounded-sm border-zinc-300 h-8 text-sm" />
            <Input placeholder="Owner / company" value={row.owner || ""} onChange={(e) => setRow(i, { owner: e.target.value })} disabled={!canEdit} className="rounded-sm border-zinc-300 h-8 text-sm" />
            <Input type="date" value={row.wof_expiry || ""} onChange={(e) => setRow(i, { wof_expiry: e.target.value })} disabled={!canEdit} className="rounded-sm border-zinc-300 h-8 text-sm" />
            {canEdit && (
              <button onClick={() => remove(i)} className="text-zinc-400 hover:text-red-600" aria-label="Remove">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
      {canEdit && (
        <Button type="button" onClick={add} variant="outline" className="rounded-sm w-full mt-3 uppercase font-bold text-xs">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add plant
        </Button>
      )}
    </Section>
  );
}
